import { School, Network, Building2, Globe2, ArrowRight } from "lucide-react";
import { Link } from "react-router-dom";
import { PageHero, SectionHeading, CTASection } from "../components/site/Shared";
import { Reveal, Stagger, StaggerItem } from "../components/site/Reveal";
import { CommandCenterMockup } from "../components/site/Mockups";
import { ROLES } from "./SolutionRole";
import { Zoomable } from "../components/site/Zoomable";

const ROLE_ORDER = ["teacher", "student", "parent", "principal", "admin"];

const solutions = [
  {
    icon: School, title: "Single Schools",
    desc: "One school gets a complete operating layer — from classroom capture to leadership visibility.",
    points: ["Classroom capture", "Parent communication", "Principal oversight"],
  },
  {
    icon: Network, title: "School Groups & Chains",
    desc: "Central visibility, governance and standardised workflows across campuses.",
    points: ["Multi-campus governance", "Comparable analytics", "Central audit trail"],
  },
  {
    icon: Building2, title: "Boards & Trusts",
    desc: "Network-level oversight, reporting and accountability.",
    points: ["Governance dashboards", "Outcome reporting", "Role-based access"],
  },
  {
    icon: Globe2, title: "School Networks",
    desc: "Architecture designed for large, distributed school ecosystems.",
    points: ["Localised workflows", "Subscription management", "Enterprise architecture"],
  },
];

export default function Solutions() {
  return (
    <>
      <PageHero
        overline="Solutions"
        title="Built for every scale of school."
        sub="Designed to scale from a single school to multi-campus and multi-region networks — the same governed operating layer, adapted to context."
      />

      <section className="py-16 md:py-24" data-testid="solutions-list">
        <div className="max-w-7xl mx-auto px-5 sm:px-8">
          <Stagger className="grid md:grid-cols-2 gap-4">
            {solutions.map((s) => (
              <StaggerItem key={s.title}>
                <div className="glass glass-hover rounded-3xl p-8 h-full" data-testid={`solution-${s.title.split(" ")[0].toLowerCase()}`}>
                  <span className="h-11 w-11 rounded-xl bg-[#F1BF26]/12 flex items-center justify-center">
                    <s.icon size={22} className="text-[#F1BF26]" />
                  </span>
                  <h3 className="font-heading text-2xl font-medium mt-5 tracking-tight text-white">{s.title}</h3>
                  <p className="text-[#9DA9BA] mt-3 leading-relaxed">{s.desc}</p>
                  <div className="flex flex-wrap gap-2 mt-6">
                    {s.points.map((p) => (
                      <span key={p} className="text-xs font-mono-accent uppercase tracking-wider text-[#C9B17B] border border-white/10 rounded-full px-3 py-1">{p}</span>
                    ))}
                  </div>
                </div>
              </StaggerItem>
            ))}
          </Stagger>
        </div>
      </section>

      <section className="chrome py-16 md:py-20 border-t border-white/8" data-testid="solutions-by-role">
        <div className="max-w-7xl mx-auto px-5 sm:px-8">
          <SectionHeading overline="By role" title="Different experiences. One operating layer."
            sub="Each stakeholder gets a purpose-built experience — every experience runs on the same underlying operational data." />
          <div className="grid sm:grid-cols-2 lg:grid-cols-5 gap-3 mt-8">
            {ROLE_ORDER.map((k) => {
              const r = ROLES[k];
              return (
                <Link key={k} to={`/solutions/${k}`} data-testid={`solutions-role-${k}`}
                  className="glass glass-hover rounded-2xl p-5 group">
                  <span className="h-10 w-10 rounded-lg bg-[#F1BF26]/15 flex items-center justify-center"><r.icon size={18} className="text-[#F1BF26]" /></span>
                  <div className="text-white font-medium mt-4">{r.label}</div>
                  <div className="text-white/45 text-xs mt-1">{r.tagline}</div>
                  <span className="inline-flex items-center gap-1.5 text-[#F1BF26] text-[13px] font-medium mt-4 group-hover:gap-2.5 transition-all">Explore <ArrowRight size={13} /></span>
                </Link>
              );
            })}
          </div>
        </div>
      </section>

      <section className="chrome py-20 relative overflow-hidden border-t border-white/8">
        <div className="relative max-w-7xl mx-auto px-5 sm:px-8 grid lg:grid-cols-2 gap-12 items-center">
          <Reveal>
            <SectionHeading overline="Deployment" title="From fragmented tools to connected operations."
              sub="Every deployment moves a school from disconnected systems to one governed operating layer — with role-based access, communication and analytics on the same foundation." />
          </Reveal>
          <Reveal delay={0.1}><Zoomable label="Principal Command Center"><CommandCenterMockup live /></Zoomable></Reveal>
        </div>
      </section>

    </>
  );
}
